import { Button, IconButton, Stack, Typography } from '@mui/material';
import { SyncUser } from '@schooly/api';
import { INVITE_ID_SEARCH_PARAM, SchoolUserRole } from '@schooly/constants';
import {
  ArrowRightIcon,
  CrossIcon,
  ModalConfirm,
  ModalContent,
  ModalFooter,
  ModalHeaderActions,
  PendingInviteSvg,
} from '@schooly/style';
import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

type PendingInviteModalProps = {
  user: SyncUser;
};

export const PendingInviteModal: FC<PendingInviteModalProps> = ({ user }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAcceptingInvite = Boolean(searchParams.get(INVITE_ID_SEARCH_PARAM));

  const [shownInvites, setShownInvites] = useState<string[]>([]);

  const pendingInvites = useMemo(() => {
    if (!user.invites?.length) return [];

    return user.invites
      .filter((invite) => invite.email === user.account_email)
      .sort((a, b) => a.school.name.localeCompare(b.school.name));
  }, [user]);

  const invite = useMemo(() => {
    const invitesToShow = pendingInvites.filter((invite) => !shownInvites.includes(invite.id));
    return invitesToShow.length > 0 ? invitesToShow[0] : null;
  }, [pendingInvites, shownInvites]);

  const handleClose = (id: string) => setShownInvites((prev) => [...prev, id]);

  const handleAccept = (id: string) => {
    navigate(`/?${INVITE_ID_SEARCH_PARAM}=${id}`);
    handleClose(id);
  };

  const getInviteText = (roles: SchoolUserRole[]) => {
    let textId = 'invites-AcceptInvitation-staff';

    if (roles.includes(SchoolUserRole.Staff)) {
      return textId;
    }

    if (roles.includes(SchoolUserRole.Parent)) {
      textId = 'invites-AcceptInvitation-parent';
    }

    return textId;
  };

  if (!invite || isAcceptingInvite) return null;

  return (
    <ModalConfirm fullWidth open onClose={() => handleClose(invite.id)}>
      <ModalHeaderActions sx={{ minHeight: 0, justifyContent: 'flex-end', p: 2.5, pb: 0 }}>
        <IconButton onClick={() => handleClose(invite.id)}>
          <CrossIcon />
        </IconButton>
      </ModalHeaderActions>
      <ModalContent active sx={{ pt: 0 }}>
        <Stack justifyContent="center" alignItems="center">
          <PendingInviteSvg />
          <Typography
            variant="h1"
            align="center"
            sx={(theme) => ({ margin: theme.spacing(5, 2, 1), whiteSpace: 'pre' })}
          >
            {formatMessage({ id: 'invites-InvitesYou' }, { name: invite.school.name })}
          </Typography>
          <Typography align="center" mx={2} maxWidth={250}>
            {formatMessage({ id: getInviteText(invite.roles) })}
          </Typography>
        </Stack>
      </ModalContent>
      <ModalFooter sx={{ justifyContent: 'center', pt: 0, pb: 5 }} active withBorderTop={false}>
        <Button endIcon={<ArrowRightIcon />} onClick={() => handleAccept(invite.id)}>
          {formatMessage({ id: 'invites-AcceptInvitation' })}
        </Button>
      </ModalFooter>
    </ModalConfirm>
  );
};
